import { useContext, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  Divider,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  MenuItem,
  Stack,
} from "@mui/material"
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded"
import EditIcon from "@mui/icons-material/Edit"
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline"
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline"

import useAlertActions from "helpers/hooks/useAlertActions"
import useEntityAlertTypes from "helpers/hooks/useEntityAlertTypes"
import type { AlertSettingAccordion } from "types/alerts.types"
import ConfirmationPopup from "widgets/common/ConfirmationPopup"
import Menu from "widgets/common/Menu"
import AlertActionPopup from "widgets/alerts/AlertActionPopup"
import { AlertSettingDrawerContext } from "widgets/alerts/settings/AlertSettingDrawerContext"
import DeleteAlertSettingPopup from "widgets/alerts/settings/DeleteAlertSettingPopup"

interface AlertAccordionMenuProps {
  alert: AlertSettingAccordion
  setActive: (active: boolean) => void
  isSettingActive: boolean
}

const subheaderSx = {
  typography: "caption",
  color: "neutral.600",
  lineHeight: "2rem",
  backgroundColor: "transparent",
}

const AlertAccordionMenu = ({
  alert,
  setActive,
  isSettingActive,
}: AlertAccordionMenuProps) => {
  const { t } = useTranslation()
  const [actionPopupVisible, setActionPopupVisible] = useState<boolean>(false)
  const [selectedAction, setSelectedAction] = useState<{
    action: string
    actionKey: string
  }>({ action: "", actionKey: "" })
  const [isDisableConfirmOpen, setIsDisableConfirmOpen] = useState(false)
  const [isDeletePopupOpen, setIsDeletePopupOpen] = useState(false)
  const drawer = useContext(AlertSettingDrawerContext)
  const { alertTypes } = useEntityAlertTypes(alert.entity)
  const actions = alertTypes?.find((alertType) => alertType.name === alert.type)
    ?.actions as string[]
  const { actionItems } = useAlertActions({ actions, state: alert.state })
  const alertSettingName = alert.config.custom_name as string

  // Event actions only apply while the alert is triggered.
  const eventItems =
    alert.state === "open" || alert.state === "silenced" ? actionItems : []
  // Group settings have no form to edit them with.
  const canConfigure = alert.entity !== "group"
  const isDisabled = alert.state === "disabled"

  if (!eventItems.length && !canConfigure) return null

  const eventMenuItems = eventItems.length
    ? [
        <ListSubheader key="event-header" sx={subheaderSx}>
          {t("alerts.CURRENT_EVENT")}
        </ListSubheader>,
        ...eventItems.map((item) => (
          <MenuItem
            key={item.id}
            onClick={() => {
              setSelectedAction({ action: item.name, actionKey: item.actionKey })
              setActionPopupVisible(true)
            }}
          >
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText>{t(item.title)}</ListItemText>
          </MenuItem>
        )),
      ]
    : []
  const configurationMenuItems = canConfigure
    ? [
        <ListSubheader key="configuration-header" sx={subheaderSx}>
          {t("alerts.CONFIGURATION")}
        </ListSubheader>,
        ...(drawer
          ? [
              <MenuItem key="edit" onClick={() => drawer.openEdit(alert)}>
                <ListItemIcon>
                  <EditIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>{t("generic.EDIT")}</ListItemText>
              </MenuItem>,
            ]
          : []),
        <MenuItem
          key="toggle-active"
          disabled={isSettingActive}
          onClick={() => (isDisabled ? setActive(true) : setIsDisableConfirmOpen(true))}
        >
          <ListItemIcon>
            {isDisabled ? (
              <PlayCircleOutlineIcon fontSize="small" />
            ) : (
              <PauseCircleOutlineIcon fontSize="small" />
            )}
          </ListItemIcon>
          <ListItemText>
            {t(isDisabled ? "alerts.ENABLE" : "alerts.DISABLE")}
          </ListItemText>
        </MenuItem>,
        <Divider key="delete-divider" />,
        <MenuItem
          key="delete"
          sx={{ color: (theme) => theme.palette.error.main }}
          onClick={() => setIsDeletePopupOpen(true)}
        >
          <ListItemIcon>
            <DeleteRoundedIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>{t("generic.DELETE")}</ListItemText>
        </MenuItem>,
      ]
    : []

  return (
    <Stack>
      <Menu>
        {[
          ...eventMenuItems,
          ...(eventMenuItems.length && configurationMenuItems.length
            ? [<Divider key="groups-divider" />]
            : []),
          ...configurationMenuItems,
        ]}
      </Menu>
      <AlertActionPopup
        open={actionPopupVisible}
        onClose={() => setActionPopupVisible(false)}
        action={selectedAction}
        settingId={alert.id}
        alertName={alertSettingName}
      />
      <ConfirmationPopup
        open={isDisableConfirmOpen}
        onClose={() => setIsDisableConfirmOpen(false)}
        description={t("alerts.ARE_YOU_SURE_TO_DISABLE", { alertName: alertSettingName })}
        mainButtonText={t("alerts.DISABLE")}
        // Closes right away, since the state icon in the header shows the request.
        onConfirm={() => {
          setIsDisableConfirmOpen(false)
          setActive(false)
        }}
        isLoading={false}
      />
      {isDeletePopupOpen && (
        <DeleteAlertSettingPopup
          open={isDeletePopupOpen}
          onClose={() => setIsDeletePopupOpen(false)}
          alertSetting={alert}
          entity={alert.entity}
        />
      )}
    </Stack>
  )
}

export default AlertAccordionMenu
